import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ArrowDownTrayIcon, DocumentIcon, TrashIcon, PlusIcon, XMarkIcon, MagnifyingGlassPlusIcon } from '@heroicons/react/24/outline';
import { useToast } from '../../../contexts/ToastContext';
import repairOrdersService from '../../../services/repairOrders.service';
import type { RepairOrderDocument } from '../../../types/repairOrders';
import { DocumentTypes } from '../../../types/repairOrders';
import { saveAs } from '../../../utils/saveFile';

import {
  ALLOWED_EXTENSIONS, ALLOWED_EXTENSIONS_SET, ALLOWED_EXTENSIONS_DISPLAY,
  MAX_FILE_SIZE, MAX_FILE_SIZE_DISPLAY, IMAGE_EXTENSIONS,
} from '../../../config/fileTypes';
import ModalHeader from '../../../components/ui/ModalHeader';
import { useModalDrag } from '../../../hooks/useModalDrag';

export interface PendingFile {
  file: File;
  documentType: string;
  description?: string;
}

interface RepairOrderDocumentsProps {
  orderRecno: number | null;
  isReadOnly: boolean;
  pronumber?: number | null;
  pendingFiles?: PendingFile[];
  onPendingFilesChange?: (files: PendingFile[]) => void;
}

export default function RepairOrderDocuments({ orderRecno, isReadOnly, pronumber, pendingFiles, onPendingFilesChange }: RepairOrderDocumentsProps) {
  const { showToast } = useToast();
  const [documents, setDocuments] = useState<RepairOrderDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadDocType, setUploadDocType] = useState<string>('Photo');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  const [previewUrls, setPreviewUrls] = useState<Record<number, string>>({});
  const [lightboxDoc, setLightboxDoc] = useState<RepairOrderDocument | null>(null);
  const previewUrlsRef = useRef<Record<number, string>>({});

  // Header-drag for the image lightbox.
  const lightboxDrag = useModalDrag(!!lightboxDoc);

  const isImage = (filetype: string) => IMAGE_EXTENSIONS.has(filetype?.toLowerCase());

  const isCreateMode = orderRecno === null;

  const fetchDocuments = useCallback(async () => {
    if (!orderRecno) return;
    try {
      setLoading(true);
      const result = await repairOrdersService.getDocuments(orderRecno);
      setDocuments(result.documents);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
    } finally {
      setLoading(false);
    }
  }, [orderRecno]);

  useEffect(() => {
    if (orderRecno) {
      fetchDocuments();
    } else {
      setLoading(false);
    }
  }, [orderRecno, fetchDocuments]);

  // Fetch preview thumbnails for image documents
  useEffect(() => {
    const imageDocs = documents.filter((d) => isImage(d.filetype));
    if (imageDocs.length === 0 || !orderRecno) return;

    let cancelled = false;

    const fetchPreviews = async () => {
      const newUrls: Record<number, string> = {};
      await Promise.allSettled(
        imageDocs.map(async (doc) => {
          // Skip if we already have a URL for this document
          if (previewUrlsRef.current[doc.map_recno]) {
            newUrls[doc.map_recno] = previewUrlsRef.current[doc.map_recno];
            return;
          }
          try {
            const blob = await repairOrdersService.downloadDocument(orderRecno, doc.map_recno);
            if (!cancelled) {
              const url = URL.createObjectURL(blob);
              newUrls[doc.map_recno] = url;
            }
          } catch {
            // Silently skip failed previews
          }
        })
      );
      if (!cancelled) {
        previewUrlsRef.current = { ...previewUrlsRef.current, ...newUrls };
        setPreviewUrls((prev) => ({ ...prev, ...newUrls }));
      }
    };

    fetchPreviews();

    return () => {
      cancelled = true;
    };
  }, [documents, orderRecno]);

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      Object.values(previewUrlsRef.current).forEach(URL.revokeObjectURL);
    };
  }, []);

  const handleDownload = useCallback(async (doc: RepairOrderDocument) => {
    if (!orderRecno) return;
    setDownloading(doc.map_recno);
    try {
      await saveAs(doc.filename, () =>
        repairOrdersService.downloadDocument(orderRecno, doc.map_recno),
      );
    } catch (err) {
      showToast({ message: 'Failed to download document', type: 'error' });
    } finally {
      setDownloading(null);
    }
  }, [orderRecno, showToast]);

  const handleUpload = useCallback(async () => {
    if (!uploadFile) return;

    const ext = uploadFile.name.split('.').pop()?.toLowerCase();
    if (!ext || !ALLOWED_EXTENSIONS_SET.has(ext)) {
      showToast({ message: `File type '.${ext || 'unknown'}' is not supported. Allowed types: ${ALLOWED_EXTENSIONS_DISPLAY}`, type: 'error' });
      return;
    }

    if (uploadFile.size > MAX_FILE_SIZE) {
      const sizeMB = (uploadFile.size / (1024 * 1024)).toFixed(1);
      showToast({ message: `File is too large (${sizeMB}MB). Maximum allowed size is ${MAX_FILE_SIZE_DISPLAY}.`, type: 'error' });
      return;
    }

    // In create mode, queue the file instead of uploading
    if (isCreateMode && onPendingFilesChange && pendingFiles) {
      onPendingFilesChange([...pendingFiles, { file: uploadFile, documentType: uploadDocType, description: uploadDescription || undefined }]);
      showToast({ message: 'Document queued — will upload when order is created', type: 'success' });
      setShowUploadForm(false);
      setUploadFile(null);
      setUploadDocType('Photo');
      setUploadDescription('');
      return;
    }

    setUploading(true);
    try {
      await repairOrdersService.uploadDocument(
        orderRecno!,
        uploadFile,
        uploadDocType,
        uploadDescription || undefined,
        pronumber
      );
      showToast({ message: 'Document uploaded successfully', type: 'success' });
      setShowUploadForm(false);
      setUploadFile(null);
      setUploadDocType('Photo');
      setUploadDescription('');
      await fetchDocuments();
    } catch (err) {
      showToast({ message: 'Failed to upload document', type: 'error' });
    } finally {
      setUploading(false);
    }
  }, [uploadFile, uploadDocType, uploadDescription, orderRecno, pronumber, isCreateMode, pendingFiles, onPendingFilesChange, showToast, fetchDocuments]);

  const handleDelete = useCallback(async (mapRecno: number) => {
    if (!orderRecno) return;
    setDeleting(mapRecno);
    try {
      await repairOrdersService.deleteDocument(orderRecno, mapRecno);
      showToast({ message: 'Document removed', type: 'success' });
      setConfirmDelete(null);
      // Revoke the preview URL if it exists
      if (previewUrlsRef.current[mapRecno]) {
        URL.revokeObjectURL(previewUrlsRef.current[mapRecno]);
        delete previewUrlsRef.current[mapRecno];
      }
      await fetchDocuments();
    } catch (err) {
      showToast({ message: 'Failed to remove document', type: 'error' });
    } finally {
      setDeleting(null);
    }
  }, [orderRecno, showToast, fetchDocuments]);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
      });
    } catch {
      return '';
    }
  };

  return (
    <>
      <div className="bg-panel border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-heading">
            Documents
            {documents.length > 0 && (
              <span className="ml-2 text-xs font-normal text-muted">
                ({documents.length})
              </span>
            )}
          </h3>
          {!isReadOnly && !showUploadForm && (
            <button
              type="button"
              onClick={() => setShowUploadForm(true)}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
            >
              <PlusIcon className="h-3.5 w-3.5" />
              Add Document
            </button>
          )}
        </div>

        {/* Upload Form */}
        {showUploadForm && (
          <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-medium text-label">Upload Document</span>
              <button
                type="button"
                onClick={() => { setShowUploadForm(false); setUploadFile(null); }}
                className="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-muted mb-1">
                  File <span className="text-red-500">*</span>
                </label>
                <input
                  type="file"
                  accept={ALLOWED_EXTENSIONS}
                  onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                  className="w-full text-sm text-label file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-blue-50 file:text-blue-700 dark:file:bg-blue-900/30 dark:file:text-blue-400 hover:file:bg-blue-100"
                />
                {uploadFile && (
                  <p className="mt-1 text-xs text-muted">
                    {uploadFile.name} ({(uploadFile.size / 1024).toFixed(0)} KB)
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-muted mb-1">
                  Document Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={uploadDocType}
                  onChange={(e) => setUploadDocType(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-panel text-heading focus:ring-2 focus:ring-blue-500"
                >
                  {DocumentTypes.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-muted mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                  placeholder="Optional description..."
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-panel text-heading focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => { setShowUploadForm(false); setUploadFile(null); }}
                  className="px-3 py-1.5 text-xs font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleUpload}
                  disabled={!uploadFile || uploading}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {uploading ? 'Uploading...' : isCreateMode ? 'Add' : 'Upload'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Pending Files (create mode) */}
        {isCreateMode && pendingFiles && pendingFiles.length > 0 && (
          <div className="mb-3 space-y-2">
            {pendingFiles.map((pf, idx) => (
              <div key={idx} className="flex items-center justify-between p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-heading truncate">{pf.file.name}</p>
                  <p className="text-xs text-muted">
                    {pf.documentType}{pf.description ? ` — ${pf.description}` : ''} ({(pf.file.size / 1024).toFixed(0)} KB)
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => onPendingFilesChange?.(pendingFiles.filter((_, i) => i !== idx))}
                  className="ml-2 p-1 text-gray-400 hover:text-red-500 rounded"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Documents List */}
        {loading ? (
          <p className="text-xs text-muted">Loading documents...</p>
        ) : documents.length === 0 && !(isCreateMode && pendingFiles && pendingFiles.length > 0) ? (
          <p className="text-xs text-muted">No documents attached.</p>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {documents.map((doc) => (
              <div
                key={doc.map_recno}
                className="group relative bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
              >
                {/* Preview area */}
                {isImage(doc.filetype) ? (
                  <div
                    className="relative w-full h-32 bg-gray-200 dark:bg-gray-700 cursor-pointer"
                    onClick={() => setLightboxDoc(doc)}
                  >
                    {previewUrls[doc.map_recno] ? (
                      <img
                        src={previewUrls[doc.map_recno]}
                        alt={doc.filename}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="animate-pulse w-8 h-8 rounded-full bg-gray-300 dark:bg-gray-600" />
                      </div>
                    )}
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-scrim/0 group-hover:bg-scrim/30 transition-colors flex items-center justify-center">
                      <MagnifyingGlassPlusIcon className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                ) : (
                  <div
                    className="w-full h-32 bg-gray-100 dark:bg-gray-700 flex flex-col items-center justify-center cursor-pointer"
                    onClick={() => handleDownload(doc)}
                  >
                    <DocumentIcon className="h-10 w-10 text-gray-400 dark:text-gray-500" />
                    <span className="mt-1 text-xs font-medium text-muted uppercase">
                      {doc.filetype || 'PDF'}
                    </span>
                  </div>
                )}

                {/* Info bar */}
                <div className="p-2">
                  <p className="text-xs font-medium text-heading truncate" title={doc.filename}>
                    {doc.filename}
                  </p>
                  <p className="text-[0.6875rem] text-muted truncate">
                    {doc.documenttype}
                    {doc.description ? ` — ${doc.description}` : ''}
                    {formatDate(doc.uploaddatetime) ? ` · ${formatDate(doc.uploaddatetime)}` : ''}
                  </p>
                </div>

                {/* Action buttons overlay */}
                <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleDownload(doc); }}
                    disabled={downloading === doc.map_recno}
                    className="p-1 bg-white/90 dark:bg-gray-800/90 text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 rounded shadow-sm transition-colors"
                    title="Download"
                  >
                    <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                  </button>
                  {!isReadOnly && (
                    confirmDelete === doc.map_recno ? (
                      <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={() => handleDelete(doc.map_recno)}
                          disabled={deleting === doc.map_recno}
                          className="px-1.5 py-0.5 text-[0.625rem] font-medium text-white bg-red-600 rounded shadow-sm hover:bg-red-700 disabled:opacity-50 transition-colors"
                        >
                          {deleting === doc.map_recno ? '...' : 'Delete'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmDelete(null)}
                          className="px-1.5 py-0.5 text-[0.625rem] font-medium text-gray-700 dark:text-gray-200 bg-white/90 dark:bg-gray-700/90 rounded shadow-sm hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                        >
                          No
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setConfirmDelete(doc.map_recno); }}
                        className="p-1 bg-white/90 dark:bg-gray-800/90 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded shadow-sm transition-colors"
                        title="Remove"
                      >
                        <TrashIcon className="h-3.5 w-3.5" />
                      </button>
                    )
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      {lightboxDoc && previewUrls[lightboxDoc.map_recno] && (
        <div
          className="fixed inset-0 z-[300] flex items-center justify-center bg-scrim/80"
          onClick={() => setLightboxDoc(null)}
        >
          <div
            ref={lightboxDrag.panelRef}
            style={lightboxDrag.panelStyle}
            className="relative max-w-[90vw] max-h-[90vh] rounded-lg overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader
              title={lightboxDoc.filename}
              onClose={() => setLightboxDoc(null)}
              dragHandleProps={lightboxDrag.dragHandleProps}
              actions={(
                <button
                  type="button"
                  onClick={() => handleDownload(lightboxDoc)}
                  className="p-1.5 text-slate-300 hover:text-white bg-white/10 hover:bg-white/20 rounded transition-colors"
                  title="Download"
                >
                  <ArrowDownTrayIcon className="h-5 w-5" />
                </button>
              )}
            />
            <img
              src={previewUrls[lightboxDoc.map_recno]}
              alt={lightboxDoc.filename}
              className="max-w-full max-h-[calc(90vh-4rem)] object-contain"
            />
          </div>
        </div>
      )}
    </>
  );
}
